import { useEffect, useState } from 'react'

/**
 * Scroll-spy, as a progressive enhancement.
 *
 * The design recomputed the active section on every scroll event, rAF-throttled,
 * by reading `offsetTop` for every section — a forced layout per frame per
 * section, charged against the INP <= 200 ms budget. `IntersectionObserver` does
 * the same job off the main thread and reports only on change.
 *
 * Returns `null` on the server and until the first observer callback, so the
 * prerendered HTML carries no `aria-current` and nothing shifts when one appears.
 */

/**
 * Top inset matches the sticky header so a section counts as active once its
 * heading clears the bar; the bottom inset keeps the section that owns the upper
 * half of the viewport active rather than handing over the moment the next one
 * peeks in.
 */
const ROOT_MARGIN = '-88px 0px -55% 0px'

export function useActiveSection(sectionIds: readonly string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null)

  // Depend on the joined string, not the array: a caller passing a fresh literal
  // each render would otherwise tear the observer down and rebuild it every pass.
  const key = sectionIds.join(',')

  useEffect(() => {
    const ids = key === '' ? [] : key.split(',')
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)

    if (elements.length === 0) {
      return undefined
    }

    const intersecting = new Set<string>()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            intersecting.add(entry.target.id)
          } else {
            intersecting.delete(entry.target.id)
          }
        }

        // `ids` is in document order, so the first match is the topmost section.
        setActiveId(ids.find((id) => intersecting.has(id)) ?? null)
      },
      { rootMargin: ROOT_MARGIN, threshold: 0 },
    )

    for (const element of elements) {
      observer.observe(element)
    }

    return () => {
      observer.disconnect()
    }
  }, [key])

  return activeId
}
